/**
 * Message — 渲染一条 CortexMessage（shared streamDecoder fold 出来的会话模型）。
 *
 * 和 desktop 同模型、不同视图：手机紧凑版气泡。assistant 文本走 streamdown
 * markdown，tool 调用是可展开的卡片，文件走 RemoteFile。
 */

import { useState } from 'react'

import type { CortexMessage, ToolUseBlock } from '@cowork/sessionModel'
import { unwrapDispatchTask } from '@cowork/dispatchTaskWrapper'

import Markdown from './Markdown'
import RemoteFile from './RemoteFile'
import { IconTool } from './icons'
import { useLang } from '../contexts/LanguageContext'

export default function Message({ message }: { message: CortexMessage }) {
  if (message.role === 'user') return <UserMessage message={message} />
  if (message.role === 'system') return <SystemMessage message={message} />
  return <AssistantMessage message={message} />
}

function FileRow({ files }: { files: NonNullable<CortexMessage['files']> }) {
  return (
    <div className="flex flex-col gap-1.5">
      {files.map((f) => (
        <RemoteFile
          key={f.id}
          uploadId={f.id}
          fallbackName={f.filename || undefined}
          fallbackKind={f.format === 'image' ? 'image' : 'file'}
          fromDesktop={f.type === 'artifact'}
        />
      ))}
    </div>
  )
}

function UserMessage({ message }: { message: CortexMessage }) {
  const files = message.files ?? []
  // session reload 从 SDK jsonl 拉的 user 消息会带 <dispatch> wrapper — 解掉再显示。
  const dispatched = unwrapDispatchTask(message.content || '')
  const content = dispatched ? dispatched.body : message.content
  if (!content && files.length === 0) return null
  return (
    <div className="flex flex-col items-end gap-1.5">
      {files.length > 0 && <div className="w-[82%]"><FileRow files={files} /></div>}
      {content && (
        <div className="max-w-[82%] whitespace-pre-wrap break-words rounded-xl rounded-br-sm border border-accent/20 bg-accent/12 px-3.5 py-2.5 text-sm text-primary">
          {content}
        </div>
      )}
    </div>
  )
}

function SystemMessage({ message }: { message: CortexMessage }) {
  if (!message.content) return null
  return <div className="px-2 py-1 text-center text-xs text-dimmed">{message.content}</div>
}

function AssistantMessage({ message }: { message: CortexMessage }) {
  const blocks = message.contentBlocks ?? []
  const thinking = blocks
    .filter((b): b is { type: 'thinking'; thinking: string } => b.type === 'thinking')
    .map((b) => b.thinking)
    .join('\n')
  const tools = blocks.filter((b): b is ToolUseBlock => b.type === 'tool_use')
  const files = message.files ?? []
  const isError = message.status === 'error'

  if (!message.content && !thinking && tools.length === 0 && files.length === 0) return null

  return (
    <div className="flex justify-start">
      <div className="flex w-[90%] flex-col gap-1.5">
        {thinking && <ThinkingBlock text={thinking} />}
        {message.content && (
          isError
            ? <div className="rounded-xl rounded-bl-sm border border-error/30 bg-error/10 px-3.5 py-2.5 text-sm text-error break-words">{message.content}</div>
            : <div className="rounded-xl rounded-bl-sm bg-panel px-3.5 py-2.5"><Markdown content={message.content} /></div>
        )}
        {tools.map((tb) => <ToolCard key={tb.id} block={tb} />)}
        {files.length > 0 && <FileRow files={files} />}
      </div>
    </div>
  )
}

function ThinkingBlock({ text }: { text: string }) {
  const { lang } = useLang()
  const [open, setOpen] = useState(false)
  return (
    <div>
      <button onClick={() => setOpen((v) => !v)} className="text-xs text-faint">
        {open ? '▾ ' : '▸ '}{lang === 'zh' ? '思考过程' : 'Thinking'}
      </button>
      {open && (
        <div className="mt-1 whitespace-pre-wrap break-words rounded-lg bg-panel/50 px-3 py-2 text-xs text-dimmed">
          {text}
        </div>
      )}
    </div>
  )
}

function ToolCard({ block }: { block: ToolUseBlock }) {
  const [open, setOpen] = useState(false)
  const input = stripRaw(block.input || {})
  const hasDetail = !!block.output || Object.keys(input).length > 0

  return (
    <div className="rounded-lg border border-line-subtle bg-panel/60">
      <button
        onClick={() => hasDetail && setOpen((v) => !v)}
        className="flex w-full items-center gap-1.5 px-2.5 py-1.5 text-left"
      >
        <IconTool className="h-3.5 w-3.5 shrink-0 text-faint" />
        <span className="flex-1 truncate font-mono text-xs text-dimmed">{block.label || block.name}</span>
        <span
          className={`h-1.5 w-1.5 shrink-0 rounded-full ${
            block.status === 'completed' ? 'bg-success' : 'bg-accent-light breathe'
          }`}
        />
      </button>
      {open && (
        <div className="space-y-1.5 border-t border-line-subtle px-2.5 py-2">
          {Object.keys(input).length > 0 && (
            <pre className="overflow-x-auto whitespace-pre-wrap break-words text-[11px] text-muted">
              {JSON.stringify(input, null, 2)}
            </pre>
          )}
          {block.output && (
            <pre className="overflow-x-auto whitespace-pre-wrap break-words text-[11px] text-faint">
              {block.output.slice(0, 2000)}
            </pre>
          )}
        </div>
      )}
    </div>
  )
}

/** 去掉 decoder 流式累积用的 __raw 字段（不给用户看）。 */
function stripRaw(input: Record<string, unknown>): Record<string, unknown> {
  const rest = { ...input }
  delete rest.__raw
  return rest
}
